import React, {
  useState,
  useEffect,
  useRef,
  MutableRefObject,
  useLayoutEffect,
  useMemo,
} from 'react'

import { ProjectListItem } from '../ProjectListItem'
import DisabledProjectItem from './DisabledProjectItem'

import {
  ListWrapper,
  DisabledProjectList,
  Separator,
} from './ProjectsList.styles'

import { ProjectLink } from '../../types'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { ProjectsById } from '../../operations'

export interface ProjectListProps {
  pinned?: ProjectLink[]
  projects: ProjectLink[]
  projectsById: ProjectsById
  canLoadMore?: boolean
  onLoadMore: () => Promise<any>
  onAdd?: () => void
  workspace: WorkspaceMinimalFragment
  refToLast?: HTMLDivElement
  autofocusLastCreated?: MutableRefObject<boolean>
  isDisabled?: boolean
  defaultOpen: (id: string) => boolean
  onOpenChange: (id: string, open: boolean) => void
}

/**
 * ProjectsList
 *
 * This component visually show the list of the projects with the options button on hover.
 *
 * @param props
 * @param {TextLink[]} props.items - The list of project projects.
 * @param {function} props.onDelete - OnClick handler for the Delete button inside the Dropdown.
 *
 * @example // Basic example
 * <List
 *  items={[{id: '1', name: 'Project 1', link: '/project/1'}]}
 *  onDelete={(id) => { delete(id)}}/>
 */
const ProjectsList: React.FC<ProjectListProps> = props => {
  const {
    projects,
    pinned = [],
    canLoadMore,
    onLoadMore,
    workspace,
    autofocusLastCreated,
    isDisabled,
    defaultOpen,
    onOpenChange,
    projectsById,
  } = props

  // We are setting the same ref to every project in the list. In this way, is easy to focus
  // in the last one created
  const refToLast = useRef() as React.MutableRefObject<HTMLElement>

  // We want to know that we edited an item
  // and assure that the item has been rendered
  const [editedItem, setEditedItem] = useState('')

  useEffect(() => {
    if (!autofocusLastCreated?.current) return

    refToLast?.current?.focus()
  }, [projects.length, autofocusLastCreated])

  useLayoutEffect(() => {
    if (editedItem !== '') {
      refToLast?.current?.focus()
      setEditedItem('')
    }
  }, [editedItem])

  const openRefs = useRef({ defaultOpen, onOpenChange })
  openRefs.current = { defaultOpen, onOpenChange }

  const getProject = (id: string | undefined) => {
    if (!id) return null
    return projectsById[id]?.project || null
  }
  const getProjectRef = useRef(getProject)
  getProjectRef.current = getProject

  const pinnedItems = useMemo(() => {
    const { defaultOpen, onOpenChange } = openRefs.current
    const getProject = getProjectRef.current

    return pinned.length ? (
      <>
        {pinned.map(({ link, project, nestedProjects }) => (
          <ProjectListItem
            topLevelProject={
              getProject(project.topLevelProject?.identifier) || project
            }
            key={project.identifier}
            project={project}
            link={link}
            workspace={workspace}
            refToLast={refToLast}
            setEditedItem={setEditedItem}
            nestedProjects={nestedProjects}
            onOpenChange={(id, open) => onOpenChange('pinned|' + id, open)}
            defaultOpen={id => defaultOpen('pinned|' + id)}
          />
        ))}
        <Separator key="pinned-separator" />
      </>
    ) : null
  }, [pinned, workspace])

  const projectsItems = useMemo(() => {
    const { defaultOpen, onOpenChange } = openRefs.current
    const getProject = getProjectRef.current

    return projects.map(({ link, project, nestedProjects }) => (
      <ProjectListItem
        topLevelProject={
          getProject(project.topLevelProject?.identifier) || project
        }
        key={project.identifier}
        project={project}
        link={link}
        workspace={workspace}
        refToLast={refToLast}
        setEditedItem={setEditedItem}
        nestedProjects={nestedProjects}
        isProjectList
        onOpenChange={onOpenChange}
        defaultOpen={defaultOpen}
      />
    ))
  }, [projects, workspace])

  // Inactive workspaces have the list of projects disabled
  // These items have alot less logic then the common project item
  // so we render a much simpler item with just the icon, name
  // and a tooltip for truncated names.
  if (isDisabled) {
    return (
      <DisabledProjectList>
        {projects.map(({ project }) => (
          <DisabledProjectItem key={project.identifier} project={project} />
        ))}
      </DisabledProjectList>
    )
  }

  return (
    <ListWrapper canLoadMore={canLoadMore} onLoadMore={onLoadMore}>
      {pinnedItems}
      {projectsItems}
    </ListWrapper>
  )
}

export default React.memo(ProjectsList)
